import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select";
import { formatNumber } from "@/lib/formatters";

export const PAGE_SIZES = [25, 50, 100] as const;

/** Server-side pagination controls. */
export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  fetching,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  fetching?: boolean;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  return (
    <nav aria-label="Pagination" className="flex items-center justify-between gap-3 border-t px-3 py-1.5 text-muted-foreground text-xs">
      <span className="tabular" aria-live="polite">
        {formatNumber(from)}–{formatNumber(to)} of {formatNumber(total)}
        {fetching ? " · updating…" : ""}
      </span>
      <div className="flex items-center gap-1">
        {onPageSizeChange ? (
          <label className="mr-2 flex items-center gap-1.5">
            Rows
            <NativeSelect size="sm" value={String(pageSize)} onChange={(e) => onPageSizeChange(Number(e.target.value))} aria-label="Rows per page">
              {PAGE_SIZES.map((s) => (
                <NativeSelectOption key={s} value={s}>
                  {s}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </label>
        ) : null}
        <Button size="icon-xs" variant="ghost" aria-label="First page" disabled={page <= 1} onClick={() => onPageChange(1)}>
          <ChevronsLeft />
        </Button>
        <Button size="icon-xs" variant="ghost" aria-label="Previous page" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
          <ChevronLeft />
        </Button>
        <span className="px-1 tabular">
          Page {formatNumber(page)} / {formatNumber(pages)}
        </span>
        <Button size="icon-xs" variant="ghost" aria-label="Next page" disabled={page >= pages} onClick={() => onPageChange(page + 1)}>
          <ChevronRight />
        </Button>
        <Button size="icon-xs" variant="ghost" aria-label="Last page" disabled={page >= pages} onClick={() => onPageChange(pages)}>
          <ChevronsRight />
        </Button>
      </div>
    </nav>
  );
}
